'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useMutation } from '@tanstack/react-query';
import { validateMeeting } from '@/actions/meeting';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAnalytics, ANALYTICS_EVENTS } from '@/modules/analytics';

export default function JoinMeetingForm() {
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);
  const { capture } = useAnalytics();

  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: async (formData: FormData) => {
      const meetingId = formData.get('meetingId') as string;
      return validateMeeting(meetingId);
    },
    onSuccess: (data) => {
      if (data.success) {
        capture(ANALYTICS_EVENTS.MEETING_ID_SUBMITTED, {
          meetingId: data.meeting.meetingId,
        });
        router.push(`/${data.meeting.meetingId}`);
      } else {
        capture(ANALYTICS_EVENTS.MEETING_VALIDATION_FAILED, { error: data.error });
        // Show error from server action
        setError(data.error);
      }
    },
    onError: (err) => {
      setError('An unexpected error occurred. Please try again.');
      console.error('Mutation error:', err);
    },
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null); // Clear any previous errors
    const formData = new FormData(event.currentTarget);
    mutate(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="mb-6">
        <label
          htmlFor="meetingId"
          className="block text-sm font-medium text-gray-300 mb-2"
        >
          Meeting ID
        </label>
        <Input
          id="meetingId"
          name="meetingId"
          type="text"
          placeholder="e.g. foz-123-xyz-abc"
          className="border-zinc-800 bg-zinc-950 rounded-xl text-white placeholder:text-zinc-500 focus-visible:ring-indigo-500"
          disabled={isLoading}
        />
        {error && (
          <div className="mt-2 text-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-md p-2">
            <p className="flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-2"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              {error}
            </p>
          </div>
        )}
      </div>

      <Button
        type="submit"
        className="w-full bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:from-emerald-700 rounded-xl hover:to-green-700"
        disabled={isLoading}
      >
        {isLoading ? (
          <svg
            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
              clipRule="evenodd"
            />
          </svg>
        )}
        {isLoading ? 'Joining...' : 'Join Meeting'}
      </Button>
    </form>
  );
}
